import Breadcrumb from "@/components/Breadcrumb";
import {
  useEditFirstTierSupplier,
  useGetFirstTierSuppliers,
} from "@/hooks/supplierHooks";
import { usePermissions } from "@/hooks/usePermissions";
import { useQueryHandler } from "@/hooks/useQueryHandler";
import { Box, TextField } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import FirstTierSupplierTable from "./FirstTierSupplierTable";
import type {
  EditFirstTierSupplierPayload,
  FirstTierSupplier,
} from "@/api/supplierService";
import { ErrorAlert } from "@/utils/alert";
import { getAxiosErrorMessage } from "@/utils/healper";
import SaveFirstTierSupplierModal from "@/components/first-tier-supplier/SaveFirstTierSupplierModal";
import { useUserInfo } from "@/context/UserInfoContext";
import { checkIsBuyer } from "@/utils/roleUtils";
import { useToast } from "@/hooks/useToast";

const FirstTierSupplierManagement = () => {
  const canEdit = usePermissions().canEdit("breakdown");
  const userInfo = useUserInfo();
  const { showToast } = useToast();
  const [searchFilter, setSearchFilter] = useState<string>("");
  const [openEditModal, setOpenEditModal] = useState<boolean>(false);
  const [selectedSupplier, setSelectedSupplier] =
    useState<FirstTierSupplier | null>(null);

  const {
    data: firstTierSuppliers,
    isLoading: isLoadingFirstTierSuppliers,
    isError: isErrorFirstTierSuppliers,
  } = useGetFirstTierSuppliers({
    skip: 0,
    limit: 2000,
    buyer_code: checkIsBuyer(userInfo) ? userInfo?.buyer_code : undefined,
  });

  const {
    mutate: editFirstTierSupplier,
    isPending: isPendingEditFirstTierSupplier,
  } = useEditFirstTierSupplier();

  useQueryHandler({
    isError: isErrorFirstTierSuppliers,
    isFetching: isPendingEditFirstTierSupplier,
  });

  const filteredData = useMemo(
    () =>
      (firstTierSuppliers || [])
        .filter((supplier) => {
          if (!searchFilter) return true;
          const searchLower = searchFilter.toLowerCase().trim();
          const matchSupplierCode = supplier.supplier_code
            ?.toLowerCase()
            .includes(searchLower);
          const matchSupplierAbbr = supplier.supplier_abbr
            ?.toLowerCase()
            .includes(searchLower);
          const matchSupplierName = supplier.supplier_name
            ?.toLowerCase()
            .includes(searchLower);
          const matchBuyerId = supplier.buyer_code_list?.some((buyerId) =>
            buyerId.toLowerCase().includes(searchLower),
          );

          return (
            matchSupplierCode ||
            matchSupplierAbbr ||
            matchSupplierName ||
            matchBuyerId
          );
        })
        .sort((a, b) => {
          const aHasBuyerCode = a.buyer_code_list && a.buyer_code_list.length > 0;
          const bHasBuyerCode = b.buyer_code_list && b.buyer_code_list.length > 0;

          if (aHasBuyerCode && !bHasBuyerCode) return -1;
          if (!aHasBuyerCode && bHasBuyerCode) return 1;

          const aSupplierAbbr = a.supplier_abbr?.trim() || "";
          const bSupplierAbbr = b.supplier_abbr?.trim() || "";

          if (!aSupplierAbbr && bSupplierAbbr) return 1;
          if (aSupplierAbbr && !bSupplierAbbr) return -1;

          const supplierAbbrComparison = aSupplierAbbr.localeCompare(bSupplierAbbr);
          if (supplierAbbrComparison !== 0) return supplierAbbrComparison;

          const aSupplierCode = a.supplier_code?.trim() || "";
          const bSupplierCode = b.supplier_code?.trim() || "";
          return aSupplierCode.localeCompare(bSupplierCode);
        }),
    [firstTierSuppliers, searchFilter],
  );

  useEffect(() => {
    document.title = "First Tier Supplier | AMPA";
  }, []);

  const handleClickEdit = (supplier: FirstTierSupplier | null) => () => {
    if (!supplier) return;
    setSelectedSupplier(supplier);
    setOpenEditModal(true);
  };

  const handleClickDetail =
    (supplierCode: string | null, templateType: string | null): string | null => {
      if (!supplierCode || !templateType || templateType === "None") return null;
      switch (templateType?.toLocaleLowerCase()) {
        case "part":
          return `/breakdown/${supplierCode}`;
        case "exchange rate":
          return `/breakdown/${supplierCode}/exchange-rate`;
        default:
          return null;
      }
    };

  const handleSaveEdit = (
    supplierCode: string,
    payload: EditFirstTierSupplierPayload,
  ) => {
    editFirstTierSupplier(
      {
        supplierCode: supplierCode,
        payload: payload,
      },
      {
        onSuccess: () => {
          showToast("แก้ไขข้อมูล Supplier สำเร็จ", "success");
        },
        onError: (error) => {
          ErrorAlert("Error", getAxiosErrorMessage(error));
          console.log("Error editing supplier", error);
        },
      },
    );
  };

  return (
    <Box sx={{ px: 3, pt: 3, pb: 1 }}>
      <Breadcrumb current="breakdown" />
      <Box mt={1}>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Box sx={{ flexGrow: 1 }}>
            <TextField
              label="ค้นหา S Code APro / Supplier ID / ชื่อ / Buyer ID"
              variant="outlined"
              size="small"
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              sx={{
                mt: 2,
                mb: 1,
                width: "30%",
                "& .MuiInputLabel-root": {
                  fontSize: "0.8rem",
                  color: "#666666",
                  "&.Mui-focused": {
                    color: "primary.main",
                  },
                },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "#cccccc",
                  },
                  "&:hover fieldset": {
                    borderColor: "#999999",
                  },
                },
              }}
            />
          </Box>
          <Box></Box>
        </Box>
        <FirstTierSupplierTable
          data={filteredData}
          isLoading={isLoadingFirstTierSuppliers}
          canEdit={canEdit}
          handleClickEdit={handleClickEdit}
          handleClickDetail={handleClickDetail}
        />
      </Box>
      <SaveFirstTierSupplierModal
        open={openEditModal}
        onClose={() => setOpenEditModal(false)}
        selectedSupplier={selectedSupplier!}
        handleSaveEdit={handleSaveEdit}
      />
    </Box>
  );
};

export default FirstTierSupplierManagement;
